<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

const serviceType = ref('ongoing')
const occupancy = ref('occupied')

const isOneTime = computed(() => serviceType.value === 'one-time')
const isVacant = computed(() => occupancy.value === 'vacant')
const isStopped = computed(() => isOneTime.value || isVacant.value)

const emit = defineEmits<{
  (e: 'update:stopped', value: boolean): void
}>()

watch(isStopped, (value) => {
  emit('update:stopped', value)
}, { immediate: true })

const serviceTypeOptions = [
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'one-time', label: 'One time' },
]

const occupancyOptions = [
  { value: 'occupied', label: 'Occupied' },
  { value: 'vacant', label: 'Vacant' },
]
</script>

<template>
  <div>
    <div class="space-y-6 mb-8">
      <div class="space-y-2">
        <label class="text-xs font-bold uppercase tracking-[1.4px] text-mm-dark">
          Choose your service frequency<span class="text-mm-green">*</span>
        </label>
        <Select v-model="serviceType" default-value="ongoing">
          <SelectTrigger class="h-12 data-[state=open]:border-mm-green focus-visible:border-mm-green focus-visible:ring-mm-green/30">
            <SelectValue placeholder="Select service type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              v-for="option in serviceTypeOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div class="space-y-2">
        <label class="text-xs font-bold uppercase tracking-[1.4px] text-mm-dark">
          Is your home occupied or vacant?<span class="text-mm-green">*</span>
        </label>
        <Select v-model="occupancy" default-value="occupied" :disabled="isOneTime">
          <SelectTrigger class="h-12 data-[state=open]:border-mm-green focus-visible:border-mm-green focus-visible:ring-mm-green/30">
            <SelectValue placeholder="Select occupancy" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              v-for="option in occupancyOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>

    <div v-if="isOneTime" class="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
      <h2 class="text-lg font-bold text-mm-dark">Ongoing service only</h2>
      <p class="text-[#6d706d] text-sm mt-2">
        We do not offer one-time mows at this time.
      </p>
    </div>

    <div v-if="isVacant && !isOneTime" class="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
      <h2 class="text-lg font-bold text-mm-dark">Occupied properties only</h2>
      <p class="text-[#6d706d] text-sm mt-2">
        We only service occupied properties at this time.
      </p>
    </div>
  </div>
</template>
